import type { AmmLevel, GetCoinPropertiesResponse } from "deso-protocol";
import { getCoinProperties } from "deso-protocol";
import { useEffect, useState } from "react";

import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { desoToUSD, focusToUsd } from "@/utils/currency";

type AmmOrderbookProps = {
  publicKey: string;
  tokenPriceUsd: number;
  focusExchangeRate: number;
  desoExchangeRate: number;
  transactorPublicKeyBase58Check?: string;
};

type AmmLevelWithTransactor = AmmLevel & {
  TransactorPublicKeyBase58Check: string;
  OrderId?: string;
};

type AmmOrderbookLevel = {
  asks: AmmLevelWithTransactor[];
  bids: AmmLevelWithTransactor[];
};

const useAmmOrderbook = ({
  publicKey,
  tokenPriceUsd,
  focusExchangeRate,
  desoExchangeRate,
  transactorPublicKeyBase58Check,
}: AmmOrderbookProps) => {
  const [loading, setLoading] = useState(false);
  const [ammOrderbook, setAmmOrderbook] = useState<AmmOrderbookLevel | null>(
    null,
  );
  const [coinProperties, setCoinProperties] =
    useState<GetCoinPropertiesResponse | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!publicKey || !transactorPublicKeyBase58Check) {
        return;
      }

      const isFocus =
        publicKey === process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY;
      const convertPrice = (price: number) => {
        return isFocus
          ? desoToUSD(price, desoExchangeRate)
          : focusToUsd(price, focusExchangeRate);
      };

      setLoading(true);
      try {
        const coinPropertiesData = await getCoinProperties(
          {
            BaseCurrencyPublicKey: publicKey,
            // TODO: update deso-js library
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            IncludeLevels: true,
            IncludeOrders: true,
          },
          {
            nodeURI: process.env.NEXT_PUBLIC_AMM_URI,
          },
        );

        setCoinProperties(coinPropertiesData);

        const levels = (
          coinPropertiesData.Levels[0] as unknown as AmmLevel[]
        ).map((level) => ({
          ...level,
          TransactorPublicKeyBase58Check: transactorPublicKeyBase58Check,
        })) as AmmLevelWithTransactor[];

        setAmmOrderbook({
          asks: levels
            .filter(
              (level) =>
                convertPrice(level.PriceInQuoteCurrency) > tokenPriceUsd,
            )
            .sort((a, b) => b.PriceInQuoteCurrency - a.PriceInQuoteCurrency),
          bids: levels
            .filter(
              (level) =>
                convertPrice(level.PriceInQuoteCurrency) < tokenPriceUsd,
            )
            .sort((a, b) => b.PriceInQuoteCurrency - a.PriceInQuoteCurrency),
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: `An error occurred while fetching your amm coin properties`,
          details: createToastErrorDetails(error),
        });
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [
    focusExchangeRate,
    desoExchangeRate,
    publicKey,
    tokenPriceUsd,
    transactorPublicKeyBase58Check,
  ]);

  return { loadingAmmOrderbook: loading, coinProperties, ammOrderbook };
};

export default useAmmOrderbook;
